import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Icon,
  ListItem,
  Radio,
  Spinner,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { ChevronLeftIcon, IdCard, Mail, CalendarClock } from 'lucide-react';
import { useCallback, useState, useEffect } from 'react';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import { useBookCoachingCall } from './useBookCoachingCall';
import { skipToken } from '@tanstack/react-query';

function EnrolmentList({
  enrolments,
  onSelectEnrolment,
}: {
  enrolments: RouterOutputs['enrolments']['getByUserId'] | undefined;
  onSelectEnrolment: (id: number) => void;
}) {
  const { selectedEnrolment } = useBookCoachingCall();

  if (!enrolments) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>
            Sorry, we couldn't load course enrolments. Please refresh or try again later.
          </Trans>
        </Text>
      </Box>
    );
  }

  if (enrolments.length === 0) {
    return (
      <Box w="full">
        <Text color="fg.muted" textAlign="left" textStyle="sm">
          <Trans>No course enrolments found.</Trans>
        </Text>
      </Box>
    );
  }

  const isSingle = enrolments.length === 1;

  useEffect(() => {
    if (enrolments && enrolments.length === 1 && !selectedEnrolment) {
      const id = enrolments[0]?.id;
      if (typeof id === 'number') {
        onSelectEnrolment(id);
      }
    }
  }, [enrolments, selectedEnrolment, onSelectEnrolment]);

  return (
    <UnorderedList styleType="none" w="full" p="0" m="0" maxH="400px" overflowY="auto">
      {enrolments.map((enrolment) => (
        <EnrolmentListItem
          key={enrolment.id}
          enrolment={enrolment}
          selectedEnrolmentId={selectedEnrolment?.id}
          onSelect={onSelectEnrolment}
          isSingle={isSingle}
        />
      ))}
    </UnorderedList>
  );
}

function EnrolmentListItem({
  enrolment,
  selectedEnrolmentId,
  onSelect,
  isSingle,
}: {
  enrolment: RouterOutputs['enrolments']['getByUserId'][number];
  selectedEnrolmentId: number | undefined;
  onSelect: (id: number) => void;
  isSingle: boolean;
}) {
  const handleClick = useCallback(() => {
    onSelect(enrolment.id);
  }, [onSelect, enrolment.id]);

  return (
    <ListItem
      p="8px 12px"
      my="6px"
      border="1px solid"
      borderRadius="8px"
      borderColor="white"
      bg="bg.canvas"
      cursor="pointer"
      onClick={handleClick}
    >
      <HStack align="center" spacing="4" justifyContent="space-between" w="full">
        <HStack align="center">
          <Text color="fg.muted">{enrolment.course?.identifier}</Text>
          <Text fontWeight="medium">{enrolment.course?.title}</Text>
        </HStack>

        {!isSingle && (
          <Radio
            isChecked={selectedEnrolmentId === enrolment.id}
            onChange={handleClick}
            colorScheme="brand"
            size="md"
            name={`enrolment-radio-${enrolment.id}`}
          />
        )}
      </HStack>
    </ListItem>
  );
}

export function SelectCourse({ goToPrevStep }: { goToPrevStep: () => void }) {
  const [hasCoachingCallLink, setHasCoachingCallLink] = useState<boolean>(false);
  const { selectedStudent, selectedEnrolment, setSelectedEnrolment } = useBookCoachingCall();

  const { data: enrolments, isLoading } = trpcTenant.enrolments.getByUserId.useQuery(
    selectedStudent?.id ? { user_id: selectedStudent.id } : skipToken,
    {
      enabled: !!selectedStudent?.id,
    },
  );

  const handleSelectEnrolment = useCallback(
    (id: number) => {
      const enrolment = enrolments?.find((e) => e.id === id);
      setHasCoachingCallLink(false);

      if (enrolment) {
        if (enrolment.course?.coachingCallLink) {
          setHasCoachingCallLink(true);
        }

        setSelectedEnrolment({
          id: enrolment.id,
          course: {
            id: enrolment?.course?.id ?? '',
            identifier: enrolment?.course?.identifier ?? '',
            title: enrolment?.course?.title ?? '',
            coachingCallLink: enrolment?.course?.coachingCallLink ?? null,
          },
        });
      }
    },
    [setSelectedEnrolment, enrolments],
  );

  const handleGoToPrevStep = useCallback(() => {
    setSelectedEnrolment(null);
    goToPrevStep();
  }, [goToPrevStep]);

  const openScheduleCallLink = useCallback(() => {
    if (selectedEnrolment?.course?.coachingCallLink) {
      window.open(selectedEnrolment.course.coachingCallLink, '_blank');
    }
  }, [selectedEnrolment]);

  return (
    <FormMainContainer>
      <FormContentContainer>
        {selectedStudent && (
          <>
            <Box
              w="full"
              borderRadius="6px"
              bg="bg.muted"
              border="1px solid"
              borderColor="border.emphasis"
              padding="8px 12px"
              gap="12px"
            >
              <HStack align="center" spacing="4" mb={2}>
                <Text fontWeight="medium">
                  <Trans>Booking for</Trans> {selectedStudent.firstName} {selectedStudent.lastName}
                </Text>
                {selectedStudent.universal_student_identifier ? (
                  <HStack align="center" spacing="1" gap={2}>
                    <Icon as={IdCard} boxSize="16px" />
                    <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                      {selectedStudent.universal_student_identifier ?? 'N/A'}
                    </Text>
                  </HStack>
                ) : null}
                <HStack align="center" spacing="1" gap={2}>
                  <Icon as={Mail} boxSize="16px" />
                  <Text fontSize="sm" lineHeight="1.25" color="fg.muted">
                    {selectedStudent.email}
                  </Text>
                </HStack>
              </HStack>

              {isLoading ? (
                <Center h="full" w="full">
                  <Spinner color="border.accent" size="lg" />
                </Center>
              ) : (
                <EnrolmentList enrolments={enrolments} onSelectEnrolment={handleSelectEnrolment} />
              )}
            </Box>

            {selectedEnrolment && !hasCoachingCallLink ? (
              <Alert
                status="warning"
                borderRadius="8px"
                border="1px solid"
                borderColor="warning"
                justifyContent="space-between"
                alignItems="top"
              >
                <Flex alignItems="start" gap={1}>
                  <AlertIcon />
                  <Box gap={1}>
                    <AlertTitle fontSize="sm">
                      <Trans>No coaching calls available</Trans>
                    </AlertTitle>
                    <AlertDescription>
                      <Text fontSize="sm">
                        <Trans>Unfortunately, coaching calls are not available for</Trans>
                        <strong> {selectedEnrolment?.course?.identifier} </strong>
                        <Trans>at this time.</Trans>
                      </Text>
                    </AlertDescription>
                  </Box>
                </Flex>
              </Alert>
            ) : null}
          </>
        )}
      </FormContentContainer>
      <FormFooterContainer>
        <Button
          onClick={openScheduleCallLink}
          isDisabled={!hasCoachingCallLink}
          leftIcon={<CalendarClock />}
        >
          <Trans>Schedule a call</Trans>
        </Button>
        <Button onClick={handleGoToPrevStep} variant="secondary" leftIcon={<ChevronLeftIcon />}>
          <Trans>Back</Trans>
        </Button>
      </FormFooterContainer>
    </FormMainContainer>
  );
}
